/**
 * Browser-side acquisition attribution helpers.
 *
 * Captures and persists the first-touch UTM context for the session
 * (so a user who signs up from the homepage AFTER landing via
 * ?utm_source=ig still gets the credit on their signup row).
 *
 * Used by every waitlist form on the site — keep the surface tiny.
 */
const STORAGE_KEY = "blendsafe.attribution.v1";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

function safeLocalGet() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); }
  catch (_e) { return null; }
}

function safeLocalSet(v) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); }
  catch (_e) { /* private mode / quota — fine to skip */ }
}

/**
 * Reads UTM params from the CURRENT URL. If any are present, persists
 * them as the canonical first-touch attribution for the session.
 * Returns the persisted attribution context (or the freshly captured
 * one, if this is the user's first ping).
 */
export function captureAttribution() {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search || "");
  const fresh = {};
  let any = false;
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) {
      fresh[k] = v.slice(0, 120);
      any = true;
    }
  });
  // Source query param ("?source=instagram") — used by hand-placed
  // marketing links pre-dating proper UTMs. Preserved verbatim.
  const src = params.get("source");
  if (src) {
    fresh.source = src.slice(0, 60);
    any = true;
  }

  const existing = safeLocalGet();
  if (any) {
    const next = {
      ...fresh,
      referrer: document.referrer || existing?.referrer || "",
      landing_path: window.location.pathname || "/",
      captured_at: new Date().toISOString(),
    };
    safeLocalSet(next);
    return next;
  }
  if (existing) return existing;

  // No UTM context — still record referrer + landing path so we know
  // where the user entered the site.
  const minimal = {
    referrer: document.referrer || "",
    landing_path: window.location.pathname || "/",
    captured_at: new Date().toISOString(),
  };
  // Only persist minimal if nothing was stored yet — don't overwrite
  // a real UTM context with a downstream pageview's empty one.
  if (!existing) safeLocalSet(minimal);
  return minimal;
}

/**
 * Returns the attribution context to attach to a waitlist signup.
 * Always safe to call — falls back to empty fields when nothing was
 * captured (admin breakdown will show "(none)" for unattributed rows).
 */
export function getAttribution() {
  if (typeof window === "undefined") return {};
  const stored = safeLocalGet() || {};
  return {
    utm_source: stored.utm_source || null,
    utm_medium: stored.utm_medium || null,
    utm_campaign: stored.utm_campaign || null,
    utm_content: stored.utm_content || null,
    utm_term: stored.utm_term || null,
    referrer: stored.referrer || (typeof document !== "undefined" ? document.referrer : "") || null,
    landing_path: stored.landing_path || (typeof window !== "undefined" ? window.location.pathname : "/") || null,
  };
}
